import { PALETTE } from "../core/Theme";
import type { Frame } from "./Frame";
import type { T50Event, T50Result } from "../strategies/t50/T50Engine";
import type { StructureResult } from "../structure/types";
import type { TwoBOneAEvent } from "../structure/twoBOneA/TwoBOneAEngine";

export const T50_COLOR = { bull: "#34D399", bear: "#FB7185", pending: "#94A3B8", hedge: "#F5A623" };

/** Marker anchor for a T50 event (px). Bull markers sit under Bar 2's low, bear markers above its high. */
export function t50MarkerXY(f: Frame, ev: T50Event) {
  return { x: f.x(ev.index), y: ev.dir === "bull" ? f.y(ev.bar2.low) + 9 : f.y(ev.bar2.high) - 9 };
}

/** Hit-test T50 markers (used by the chart click → detail card). */
export function t50HitAt(f: Frame, t50: T50Result | null | undefined, x: number, y: number): T50Event | null {
  if (!t50) return null;
  let best: T50Event | null = null, bd = 10;
  for (const ev of t50.events) {
    if (ev.index < f.start || ev.index > f.end) continue;
    const p = t50MarkerXY(f, ev), d = Math.hypot(p.x - x, p.y - y);
    if (d < bd) { bd = d; best = ev; }
  }
  return best;
}

// T50 lifecycle on the chart — lightweight: a small triangle per confirmed decision bar, a state glyph
// (✓ success · × failed · ↔ hedged · ● active) and text labels only for the most recent events.
function renderT50(f: Frame, t50: T50Result) {
  const { ctx } = f;
  const visible = t50.events.filter((ev) => ev.index >= f.start && ev.index <= f.end);
  const labelFrom = Math.max(0, visible.length - 3);
  ctx.font = `bold 8px ${PALETTE.mono}`;
  ctx.textAlign = "center";
  visible.forEach((ev, i) => {
    const up = ev.dir === "bull", col = T50_COLOR[ev.dir], { x, y } = t50MarkerXY(f, ev);
    ctx.fillStyle = col; ctx.globalAlpha = ev.state === "FAILED" || ev.state === "HEDGED" ? 0.6 : 0.95;
    ctx.beginPath();
    if (up) { ctx.moveTo(x, y - 4); ctx.lineTo(x - 3.5, y + 2); ctx.lineTo(x + 3.5, y + 2); } else { ctx.moveTo(x, y + 4); ctx.lineTo(x - 3.5, y - 2); ctx.lineTo(x + 3.5, y - 2); }
    ctx.closePath(); ctx.fill();
    const glyph = ev.state === "SUCCESS" ? "✓" : ev.state === "HEDGED" ? "↔" : ev.state === "FAILED" ? "×" : ev.state === "ACTIVE" ? "●" : "";
    if (glyph) {
      ctx.fillStyle = ev.state === "SUCCESS" ? T50_COLOR.bull : ev.state === "HEDGED" ? T50_COLOR.hedge : ev.state === "FAILED" ? T50_COLOR.bear : col;
      ctx.textBaseline = up ? "top" : "bottom";
      ctx.fillText(glyph, x, y + (up ? 4 : -4));
    }
    if (i >= labelFrom && f.candleW >= 4) {
      ctx.fillStyle = col; ctx.textBaseline = up ? "top" : "bottom";
      ctx.fillText(`T50${up ? "↑" : "↓"}`, x, y + (up ? 13 : -13));
    }
    if (ev.state === "ACTIVE" && ev.entryPrice != null) { // entry tick line while the 10-tick window is running
      const ex = f.x(ev.entryIndex!), ey = f.y(ev.entryPrice);
      ctx.strokeStyle = col; ctx.setLineDash([2, 2]); ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(ex, ey); ctx.lineTo(Math.min(f.L.plotRight, f.x(ev.entryIndex! + t50.config.window)), ey); ctx.stroke(); ctx.setLineDash([]);
    }
  });
  ctx.globalAlpha = 1;
  const p = t50.pending;
  if (p && p.index >= f.start && p.index <= f.end) {
    const c = f.candles[p.index];
    if (c) {
      const x = f.x(p.index), y = f.y(c.high) - 6;
      ctx.fillStyle = p.wouldConfirm ? T50_COLOR[p.dir!] : T50_COLOR.pending; ctx.globalAlpha = 0.85;
      ctx.textBaseline = "bottom"; ctx.textAlign = "center";
      ctx.fillText(`T50 pending${p.closeLoc != null ? ` ${(p.closeLoc * 100).toFixed(0)}%` : ""}`, x, y);
      ctx.globalAlpha = 1;
    }
  }
}

// 2B1A swing markers: filled dot + "2B1A H"/"2B1A L" label, anchored to the candidate candle's high/low.
// When classic SH/SL is also drawn on the same candle, the 2B1A marker steps outward by a scale-aware offset.
function render2B1A(f: Frame, r: NonNullable<StructureResult["twoBOneA"]>, classic: StructureResult | null) {
  const { ctx } = f;
  const shIdx = new Set(classic?.highs.map((h) => h.index)), slIdx = new Set(classic?.lows.map((l) => l.index));
  const radius = Math.max(2.5, Math.min(4, f.candleW * 0.35)), showLabel = f.candleW >= 3;
  ctx.font = `bold 9px ${PALETTE.mono}`; ctx.textAlign = "center";
  const draw = (ev: TwoBOneAEvent, up: boolean, bump: boolean) => {
    const x = f.x(ev.index), dir = up ? -1 : 1;
    const y = f.y(ev.price) + dir * ((bump ? 16 : 5) + radius);
    ctx.fillStyle = up ? "#E5484D" : "#30A46C";
    ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2); ctx.fill();
    if (showLabel) { ctx.textBaseline = up ? "bottom" : "top"; ctx.fillText(up ? "2B1A H" : "2B1A L", x, y + dir * (radius + 2)); }
  };
  for (const ev of r.highs) if (ev.index >= f.start && ev.index <= f.end) draw(ev, true, shIdx.has(ev.index));
  for (const ev of r.lows) if (ev.index >= f.start && ev.index <= f.end) draw(ev, false, slIdx.has(ev.index));
}

export function renderStructure(f: Frame) {
  const s = f.opts.structure;
  const fl = f.opts.structureFlags;
  if (!s) return;
  const { ctx } = f;

  if (fl.fvg) {
    for (const g of s.fvg) {
      if (g.index < f.start - 2 || g.index > f.end + 40) continue;
      const x1 = f.x(g.index - 2), x2 = f.x(Math.min(f.end, g.index + 20));
      const yt = f.y(g.top), yb = f.y(g.bottom);
      ctx.fillStyle = g.dir === "bull" ? "rgba(0,192,118,0.12)" : "rgba(255,59,48,0.12)";
      ctx.fillRect(x1, yt, x2 - x1, yb - yt);
    }
  }
  if (fl.ob) {
    for (const b of s.obs) {
      if (b.index < f.start - 2 || b.index > f.end) continue;
      const x1 = f.x(b.index), x2 = f.x(f.end);
      const yt = f.y(b.top), yb = f.y(b.bottom);
      ctx.fillStyle = b.dir === "bull" ? "rgba(0,192,118,0.16)" : "rgba(255,59,48,0.16)";
      ctx.fillRect(x1, yt, x2 - x1, yb - yt);
    }
  }
  if (fl.liq) {
    ctx.font = `bold 10px ${PALETTE.mono}`;
    for (const lv of s.liquidity) {
      const first = lv.indices[0];
      if (first > f.end) continue;
      const x1 = f.x(Math.max(f.start, first)), x2 = f.x(f.end), y = f.y(lv.price);
      ctx.strokeStyle = lv.kind === "EQH" ? "rgba(255,82,71,0.8)" : "rgba(0,230,138,0.8)";
      ctx.setLineDash([2, 3]); ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = ctx.strokeStyle;
      ctx.textAlign = "right"; ctx.textBaseline = lv.kind === "EQH" ? "bottom" : "top";
      ctx.fillText(lv.kind, x2 - 2, y + (lv.kind === "EQH" ? -2 : 2));
    }
  }
  if (fl.swings) {
    ctx.font = `10px ${PALETTE.mono}`;
    ctx.textAlign = "center";
    ctx.fillStyle = PALETTE.bearFill; ctx.textBaseline = "bottom";
    for (const sw of s.highs) if (sw.index >= f.start && sw.index <= f.end) ctx.fillText("SH", f.x(sw.index), f.y(sw.price) - 3);
    ctx.fillStyle = PALETTE.bullFill; ctx.textBaseline = "top";
    for (const sw of s.lows) if (sw.index >= f.start && sw.index <= f.end) ctx.fillText("SL", f.x(sw.index), f.y(sw.price) + 3);
  }
  if (fl.twoBOneA && s.twoBOneA) render2B1A(f, s.twoBOneA, !!fl.swings ? s : null);
  if (fl.cisd && s.cisd) {
    ctx.font = `bold 9px ${PALETTE.mono}`;
    for (const ev of s.cisd.events) {
      if (ev.index < f.start - 5 || ev.from > f.end) continue;
      const x1 = f.x(ev.from), x2 = f.x(ev.index), y = f.y(ev.level), up = ev.dir === "bull";
      ctx.strokeStyle = up ? "#22D3EE" : "#F472B6"; ctx.fillStyle = ctx.strokeStyle;
      ctx.setLineDash([2, 3]); ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke(); ctx.setLineDash([]);
      const ay = y + (up ? -6 : 6);
      ctx.beginPath(); ctx.moveTo(x2, ay); ctx.lineTo(x2 - 4, ay + (up ? 6 : -6)); ctx.lineTo(x2 + 4, ay + (up ? 6 : -6)); ctx.closePath(); ctx.fill();
      ctx.textAlign = "left"; ctx.textBaseline = up ? "bottom" : "top";
      ctx.fillText("CISD", x2 + 6, y + (up ? -1 : 1));
    }
    const p = s.cisd.pending;
    if (p) {
      const y = f.y(p.level), x1 = f.x(Math.max(p.from, f.start));
      ctx.strokeStyle = p.dir === "bull" ? "#22D3EE" : "#F472B6"; ctx.globalAlpha = 0.6; ctx.setLineDash([1, 3]);
      ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(f.L.plotRight, y); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = ctx.strokeStyle; ctx.textAlign = "right"; ctx.textBaseline = p.dir === "bull" ? "bottom" : "top";
      ctx.fillText(`pending ${p.dir} CISD`, f.L.plotRight - 4, y + (p.dir === "bull" ? -1 : 1));
      ctx.globalAlpha = 1;
    }
  }
  if (fl.t50 && s.t50) renderT50(f, s.t50);
  if (fl.bos || fl.choch) {
    ctx.font = `bold 10px ${PALETTE.mono}`;
    for (const ev of s.events) {
      if (ev.type === "BOS" && !fl.bos) continue;
      if (ev.type === "CHoCH" && !fl.choch) continue;
      if (ev.from < f.start - 5 || ev.index > f.end) continue;
      const x1 = f.x(ev.from), x2 = f.x(ev.index), y = f.y(ev.price);
      ctx.strokeStyle = ev.type === "CHoCH" ? PALETTE.amber : ev.dir === "bull" ? PALETTE.bullStroke : PALETTE.bearStroke;
      ctx.setLineDash([5, 3]); ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = ctx.strokeStyle;
      ctx.textAlign = "left"; ctx.textBaseline = ev.dir === "bull" ? "bottom" : "top";
      ctx.fillText(ev.type, x2 + 3, y + (ev.dir === "bull" ? -2 : 2));
    }
  }
}
